// ============================================================
// Session store de l'espace Déploiement (recube-cicd embarqué).
//
// Auth : LOGIN SÉPARÉ (recube.token en localStorage, distinct du token Xbox du
// panel parent). Le login se fait via authService.login(email, password) qui
// tape le backend Quarkus recube-cicd.
//
// Projet actif : liste dynamique depuis GET /api/projects du backend, avec
// switcher UI (cf. ProjectSwitcher dans DeployScope). L'id sélectionné est
// persisté en localStorage sous `recube.currentProjectId`.
// ============================================================
import { create } from 'zustand';
import { ApiError, tokenStorage } from '@/deploy/services/api';
import { authService } from '@/deploy/services/auth.service';
import { usersService, type UserCreatePayload, type UserUpdatePayload } from '@/deploy/services/users.service';
import { projectsService, type ProjectCreatePayload, type ProjectUpdatePayload } from '@/deploy/services/projects.service';
import type { Project, User } from '@/deploy/types';
import { useGameStore } from '@/stores/game.store';
import { getGameById } from '@/config/games';

const PROJECT_KEY = 'recube.currentProjectId';

/**
 * Mapping ID de jeu du panel → pattern de code recherché dans project.code
 * pour l'auto-sélection au chargement / switch de jeu.
 *
 * Convention côté API recube-cicd : PBE (Paladium Bedrock), NGBE (NationsGlory Bedrock).
 * Le pattern est comparé case-insensitive et match exact prioritaire.
 */
const PROJECT_CODE_BY_GAME: Record<string, string> = {
  paladium: 'PBE',
  nationsglory: 'NGBE',
  recube: 'RC',
};

/** Trouve le projet correspondant au jeu actif via project.code. */
function findProjectForActiveGame(projects: Project[]): Project | undefined {
  const gameId = useGameStore.getState().activeGameId;
  const wanted = (PROJECT_CODE_BY_GAME[gameId] ?? '').toUpperCase();
  if (!wanted) return undefined;
  // 1. match exact sur code
  const exact = projects.find((p) => (p.code ?? '').toUpperCase() === wanted);
  if (exact) return exact;
  // 2. fallback : contient le pattern (ex: game "paladium" match projet code "PBE-old")
  return projects.find((p) => (p.code ?? '').toUpperCase().includes(wanted));
}

const PLACEHOLDER_USER: User = {
  id: '',
  name: '',
  email: '',
  role: 'viewer',
  avatarColor: '#3B82F6',
  permissions: [],
};

const PLACEHOLDER_PROJECT: Project = {
  id: '',
  name: 'Aucun projet',
  code: '—',
  description: 'Crée un projet côté API recube-cicd pour commencer.',
  color: '#3B82F6',
  dotnetVersion: '9.0.304',
  createdAt: new Date().toISOString(),
};

interface SessionState {
  user: User;
  users: User[];
  projects: Project[];
  currentProjectId: string;
  isLoggedIn: boolean;
  bootstrapping: boolean;
  loadingUsers: boolean;
  loadingProjects: boolean;

  // Auth
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ ok: true } | { ok: false; reason: string }>;
  logout: () => void;

  // Project switcher
  setProject: (projectId: string) => void;
  currentProject: () => Project;

  // Refresh
  refreshUsers: () => Promise<void>;
  refreshProjects: () => Promise<void>;

  // Users CRUD
  createUser: (input: UserCreatePayload) => Promise<User>;
  updateUser: (userId: string, patch: UserUpdatePayload) => Promise<User>;
  deleteUser: (userId: string) => Promise<void>;

  // Projects CRUD
  createProject: (input: ProjectCreatePayload) => Promise<Project>;
  updateProject: (projectId: string, patch: ProjectUpdatePayload) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  user: PLACEHOLDER_USER,
  users: [],
  projects: [],
  currentProjectId: (typeof window !== 'undefined' ? localStorage.getItem(PROJECT_KEY) : null) ?? '',
  isLoggedIn: false,
  bootstrapping: true,
  loadingUsers: false,
  loadingProjects: false,

  bootstrap: async () => {
    const token = tokenStorage.get();
    if (!token) {
      set({ bootstrapping: false });
      return;
    }
    try {
      const me = await authService.me();
      set({ user: me });
      await Promise.all([get().refreshUsers(), get().refreshProjects()]);
      set({ isLoggedIn: true });
    } catch {
      tokenStorage.clear();
      set({ user: PLACEHOLDER_USER, isLoggedIn: false });
    } finally {
      set({ bootstrapping: false });
    }
  },

  login: async (email, password) => {
    try {
      const res = await authService.login(email, password);
      tokenStorage.set(res.token);
      set({ user: res.user });
      await Promise.all([get().refreshUsers(), get().refreshProjects()]);
      set({ isLoggedIn: true });
      return { ok: true };
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Erreur réseau';
      return { ok: false, reason: msg };
    }
  },

  logout: () => {
    tokenStorage.clear();
    set({
      user: PLACEHOLDER_USER,
      users: [],
      projects: [],
      currentProjectId: '',
      isLoggedIn: false,
    });
  },

  setProject: (projectId) => {
    localStorage.setItem(PROJECT_KEY, projectId);
    set({ currentProjectId: projectId });
  },

  currentProject: () => {
    const { projects, currentProjectId } = get();
    return projects.find((p) => p.id === currentProjectId) ?? projects[0] ?? PLACEHOLDER_PROJECT;
  },

  refreshUsers: async () => {
    set({ loadingUsers: true });
    try {
      const page = await usersService.list({ pageSize: 100 });
      set({ users: page.items });
    } finally {
      set({ loadingUsers: false });
    }
  },

  refreshProjects: async () => {
    set({ loadingProjects: true });
    try {
      const items = await projectsService.list();
      // Priorité de sélection :
      //   1. Projet matchant le jeu actif du panel via PROJECT_CODE_BY_GAME (source vérité)
      //   2. Projet en localStorage si toujours présent (rollback : l'user a switché manuellement)
      //   3. Premier projet dispo
      const matchGame = findProjectForActiveGame(items);
      const current = get().currentProjectId;
      const stillExists = current && items.some((p) => p.id === current);
      const nextId = matchGame?.id ?? (stillExists ? current : (items[0]?.id ?? ''));
      if (nextId !== current) localStorage.setItem(PROJECT_KEY, nextId);
      set({ projects: items, currentProjectId: nextId });
    } finally {
      set({ loadingProjects: false });
    }
  },

  createUser: async (input) => {
    const created = await usersService.create(input);
    set((s) => ({ users: [...s.users, created] }));
    return created;
  },

  updateUser: async (userId, patch) => {
    const updated = await usersService.update(userId, patch);
    set((s) => ({
      users: s.users.map((u) => (u.id === userId ? updated : u)),
      user: s.user.id === userId ? updated : s.user,
    }));
    return updated;
  },

  deleteUser: async (userId) => {
    await usersService.remove(userId);
    set((s) => ({ users: s.users.filter((u) => u.id !== userId) }));
  },

  createProject: async (input) => {
    const created = await projectsService.create(input);
    set((s) => {
      const projects = [...s.projects, created];
      const currentProjectId = s.currentProjectId || created.id;
      if (!s.currentProjectId) localStorage.setItem(PROJECT_KEY, created.id);
      return { projects, currentProjectId };
    });
    return created;
  },

  updateProject: async (projectId, patch) => {
    const updated = await projectsService.update(projectId, patch);
    set((s) => ({ projects: s.projects.map((p) => (p.id === projectId ? updated : p)) }));
    return updated;
  },

  deleteProject: async (projectId) => {
    await projectsService.remove(projectId);
    set((s) => {
      const projects = s.projects.filter((p) => p.id !== projectId);
      const stillCurrent = projects.some((p) => p.id === s.currentProjectId);
      const currentProjectId = stillCurrent ? s.currentProjectId : (projects[0]?.id ?? '');
      if (currentProjectId !== s.currentProjectId) localStorage.setItem(PROJECT_KEY, currentProjectId);
      return { projects, currentProjectId };
    });
  },
}));

// 401 global → logout propre.
if (typeof window !== 'undefined') {
  window.addEventListener('recube:unauthenticated', () => {
    useSessionStore.getState().logout();
  });
}

// Reset stores dépendants au changement de projet / logout.
useSessionStore.subscribe((state, prev) => {
  if (state.currentProjectId !== prev.currentProjectId || (!state.isLoggedIn && prev.isLoggedIn)) {
    void import('@/deploy/stores/containers.store').then((m) => m.useContainersStore.getState().reset());
  }
});

// Switch de jeu du panel → re-sélectionne auto le projet recube-cicd matching.
if (typeof window !== 'undefined') {
  useGameStore.subscribe((gs, prev) => {
    if (gs.activeGameId === prev.activeGameId) return;
    const s = useSessionStore.getState();
    if (!s.isLoggedIn) return;
    const match = findProjectForActiveGame(s.projects);
    if (match && match.id !== s.currentProjectId) {
      localStorage.setItem(PROJECT_KEY, match.id);
      useSessionStore.setState({ currentProjectId: match.id });
    }
  });
}

// Suppress inutilisé
void getGameById;
